/**
 * Client analytics event dispatcher.
 * Integrates with Google Analytics (gtag), Google Ads conversion tracking, and Microsoft Clarity safely.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    clarity?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

export type AnalyticsEvent =
  | {
      name: "cta_click";
      properties: {
        text: string;
        href?: string;
        location?: string;
        plan?: string;
        industry?: string;
      };
    }
  | {
      name: "form_submit";
      properties: {
        formId: string;
        plan?: string;
        industry?: string;
        service?: string;
      };
    }
  | {
      name: "pdf_download";
      properties: {
        pdfName: string;
        source?: string;
      };
    }
  | {
      name: "calendly_booked";
      properties: {
        eventType?: string;
        location?: string;
        url?: string;
      };
    }
  | {
      name: "google_ads_conversion";
      properties: {
        conversionType: "form_submission" | "schedule_consultation";
        label?: string;
        value?: number;
      };
    };

// Google Ads conversion configuration
const GOOGLE_ADS_ID =
  (typeof import.meta !== "undefined" &&
    (import.meta.env?.["VITE_GOOGLE_ADS_ID"] ||
      import.meta.env?.["VITE_GOOGLE_ADS_CONVERSION_ID"])) ||
  "";

const GOOGLE_ADS_LEAD_LABEL =
  (typeof import.meta !== "undefined" && import.meta.env?.["VITE_GOOGLE_ADS_LEAD_LABEL"]) || "";

const GOOGLE_ADS_CONSULTATION_LABEL =
  (typeof import.meta !== "undefined" && import.meta.env?.["VITE_GOOGLE_ADS_CONSULTATION_LABEL"]) ||
  "";

/**
 * Push an event to window.dataLayer safely for Google Tag Manager / GA4 / Google Ads.
 */
function pushToDataLayer(payload: Record<string, unknown>): void {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
}

/**
 * Generic event tracker for GA4, Clarity, and dataLayer.
 */
export function trackEvent(
  name: AnalyticsEvent["name"],
  properties: Record<string, string | number | boolean | undefined> = {},
): void {
  if (typeof window === "undefined") return;

  // Clean undefined values
  const payload: Record<string, string | number | boolean> = {};
  for (const [key, val] of Object.entries(properties)) {
    if (val !== undefined) payload[key] = val;
  }

  // Google Analytics 4 / Google Ads (gtag)
  if (typeof window.gtag === "function") {
    window.gtag("event", name, payload);
  }

  // Google Tag Manager / dataLayer push
  pushToDataLayer({
    event: name,
    ...payload,
  });

  // Microsoft Clarity custom events
  if (typeof window.clarity === "function") {
    window.clarity("event", name);
  }

  // Optional debug log in development
  if (import.meta.env?.DEV) {
    console.info(`[Analytics Event: ${name}]`, payload);
  }
}

/**
 * Fires Google Ads conversion events, GA4 lead/consultation events, and dataLayer events.
 */
export function trackGoogleAdsConversion(
  conversionType: "form_submission" | "schedule_consultation",
  details: Record<string, unknown> = {},
): void {
  if (typeof window === "undefined") return;

  const value = typeof details["value"] === "number" ? details["value"] : 1.0;
  const currency = typeof details["currency"] === "string" ? details["currency"] : "USD";

  // Determine Google Ads send_to if ID is provided
  let sendTo: string | undefined = undefined;
  if (GOOGLE_ADS_ID) {
    const label =
      conversionType === "form_submission" ? GOOGLE_ADS_LEAD_LABEL : GOOGLE_ADS_CONSULTATION_LABEL;
    sendTo = label ? `${GOOGLE_ADS_ID}/${label}` : GOOGLE_ADS_ID;
  }

  const adsPayload: Record<string, unknown> = {
    value,
    currency,
    conversion_type: conversionType,
    ...details,
  };
  if (sendTo) {
    adsPayload["send_to"] = sendTo;
  }

  const formIdentifier =
    (typeof details["formId"] === "string" && details["formId"]) ||
    (typeof details["formName"] === "string" && details["formName"]) ||
    "lead_form";

  const consultationLocation =
    (typeof details["location"] === "string" && details["location"]) || "calendly_button";

  // 1. Fire direct Google Ads conversion event via gtag
  if (typeof window.gtag === "function") {
    window.gtag("event", "conversion", adsPayload);

    // 2. Fire recommended GA4 standard conversion events (which link directly into Google Ads)
    if (conversionType === "form_submission") {
      window.gtag("event", "generate_lead", {
        value,
        currency,
        form_name: formIdentifier,
        ...details,
      });
    } else {
      window.gtag("event", "schedule_consultation", {
        value,
        currency,
        consultation_location: consultationLocation,
        destination: "https://calendly.com/connect-finendra",
        ...details,
      });
      // Also fire standard GA4 appointment / lead event
      window.gtag("event", "book_appointment", {
        value,
        currency,
        ...details,
      });
    }
  }

  // 3. Push to window.dataLayer for GTM / Google Ads Conversion Linker
  pushToDataLayer({
    event: "conversion",
    conversion_type: conversionType,
    ...adsPayload,
  });

  if (conversionType === "form_submission") {
    pushToDataLayer({
      event: "generate_lead",
      form_id: formIdentifier,
      value,
      currency,
      ...details,
    });
  } else {
    pushToDataLayer({
      event: "schedule_consultation",
      location: consultationLocation,
      value,
      currency,
      ...details,
    });
  }

  // 4. Microsoft Clarity event
  if (typeof window.clarity === "function") {
    window.clarity(
      "event",
      conversionType === "form_submission" ? "lead_submitted" : "schedule_consultation_clicked",
    );
  }

  if (import.meta.env?.DEV) {
    console.info(`[Google Ads Conversion: ${conversionType}]`, adsPayload);
  }
}

/**
 * Track successful form submission and trigger Google Ads conversion.
 */
export function trackFormSubmission(
  formId: string,
  properties: Record<string, unknown> = {},
): void {
  // Fire general form_submit event
  trackEvent("form_submit", {
    formId,
    ...(properties as Record<string, string | number | boolean | undefined>),
  });

  // Fire Google Ads conversion
  trackGoogleAdsConversion("form_submission", {
    formId,
    ...properties,
  });
}

/**
 * Track clicks on "Schedule a Consultation" (Calendly link) and trigger Google Ads conversion.
 */
export function trackScheduleConsultation(
  location: string = "unknown",
  properties: Record<string, unknown> = {},
): void {
  // Fire general calendly event
  trackEvent("calendly_booked", {
    eventType: "consultation",
    location,
    url: "https://calendly.com/connect-finendra",
    ...(properties as Record<string, string | number | boolean | undefined>),
  });

  // Fire Google Ads conversion
  trackGoogleAdsConversion("schedule_consultation", {
    location,
    destination: "https://calendly.com/connect-finendra",
    ...properties,
  });
}
